interface LoginVersionTarget {
  orgVersion: 1 | 2;
  targetApp: 'FETT' | 'PERSEUS';
}

export type LoginVersionMismatch = LoginVersionTarget &
  (
    | { action: 'REDIRECT' | 'OPEN_WEB'; redirectUrl: string }
    | { action: 'DOWNLOAD'; downloadUrl: string }
  );

const CLIENT_DOWNLOAD_URL = 'https://chatplus.ai/zh/pages/download';

function record(input: unknown): Record<string, unknown> {
  return input !== null && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

const LOGIN_ORIGINS = {
  FETT: new Set([
    'https://mai.zhisuitech.com',
    'https://feature-stq-kjds-mai.ftest.leyantech.com',
    'https://preview-kjds-mai.ftest.leyantech.com',
    'https://sitegiant.ganjutech.com',
    'https://masteronlinechat.zhisuitech.com',
  ]),
  PERSEUS: new Set([
    'https://app.ganjutech.com',
    'https://app.chatplus.ai',
    'https://app-staging.chatplus.ai',
  ]),
};

/** 登录接续仅接受产品登录入口；客户端下载安装统一使用官网公用页面。 */
export function parseLoginVersionMismatch(input: unknown): LoginVersionMismatch | null {
  const data = record(input);
  if (data.orgVersion !== 1 && data.orgVersion !== 2) return null;
  if (data.targetApp !== 'FETT' && data.targetApp !== 'PERSEUS') return null;
  if (data.targetApp !== (data.orgVersion === 1 ? 'FETT' : 'PERSEUS')) return null;
  if (data.action === 'DOWNLOAD') {
    if (data.downloadUrl !== CLIENT_DOWNLOAD_URL) return null;
    return {
      orgVersion: data.orgVersion,
      targetApp: data.targetApp,
      action: data.action,
      downloadUrl: data.downloadUrl,
    };
  }
  if (data.action !== 'REDIRECT' && data.action !== 'OPEN_WEB') return null;
  if (typeof data.redirectUrl !== 'string') return null;
  try {
    const url = new URL(data.redirectUrl);
    if (url.username || url.password || !LOGIN_ORIGINS[data.targetApp].has(url.origin)) return null;
    const path = data.targetApp === 'FETT' ? url.hash.slice(1).split('?')[0] : url.pathname;
    if (path !== '/login') return null;
    if (data.targetApp === 'FETT' && url.pathname !== '/') return null;
    if (data.targetApp === 'PERSEUS' && url.hash) return null;
    const params =
      data.targetApp === 'FETT'
        ? new URLSearchParams(url.hash.split('?')[1] ?? '')
        : url.searchParams;
    if (data.action === 'REDIRECT' && !params.get('token')) return null;
    if (data.action === 'OPEN_WEB' && (params.has('token') || url.searchParams.has('token')))
      return null;
    return {
      orgVersion: data.orgVersion,
      targetApp: data.targetApp,
      action: data.action,
      redirectUrl: url.href,
    };
  } catch {
    return null;
  }
}

export function readLoginVersionError(error: unknown): LoginVersionMismatch | null {
  const source = record(error);
  const body = record(record(source.response).data);
  if (body.code !== 1073 && body.code !== 1075) return null;
  return parseLoginVersionMismatch(body.data);
}

export function readLoginVersionCallback(search: string): LoginVersionMismatch | null {
  const payload = new URLSearchParams(search).get('loginVersionMismatch');
  if (!payload) return null;
  try {
    return parseLoginVersionMismatch(JSON.parse(payload));
  } catch {
    return null;
  }
}

/** 按钮与计时器共用，避免两者同时触发时重复跳转。 */
export function onceLoginRedirect(redirect: () => void): () => void {
  let redirected = false;
  return () => {
    if (redirected) return;
    redirected = true;
    redirect();
  };
}
